import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import './Register.css';
import { SUPABASE_READY, supabase } from '../lib/supabaseClient';

function normalizeSpecies(speciesValue) {
  const SPECIES = [
    'Bass', 'Crappie', 'Muskie', 'Northern Pike', 'Perch',
    'Sauger', 'Trout', 'Walleye', 'Whitefish', 'Other',
  ];

  const cleaned = String(speciesValue || '').trim().replace(/\s+/g, ' ');
  if (!cleaned) return '';

  const match = SPECIES.find((item) => item.toLowerCase() === cleaned.toLowerCase());
  return match || cleaned;
}

function UserProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCatch, setSelectedCatch] = useState(null);

  const speciesProgress = useMemo(() => {
    const uniqueSpecies = new Set(
      catches.map((entry) => normalizeSpecies(entry.species).toLowerCase()).filter(Boolean)
    ).size;
    return { uniqueSpecies };
  }, [catches]);

  useEffect(() => {
    if (!SUPABASE_READY) {
      setLoading(false);
      setError('Supabase is not configured.');
      return;
    }

    async function loadUserProfile() {
      try {
        // Fetch profile by username
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('username', username)
          .maybeSingle();

        if (profileError) throw profileError;

        if (!profileData) {
          setError('User not found.');
          setLoading(false);
          return;
        }

        setProfile(profileData);

        // Fetch user's catches
        const { data: catchesData, error: catchesError } = await supabase
          .from('catches')
          .select('*')
          .eq('user_id', profileData.id);

        if (catchesError) throw catchesError;

        setCatches(catchesData || []);
        setLoading(false);
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError(err.message || 'Could not load profile.');
        setLoading(false);
      }
    }

    loadUserProfile();
  }, [username]);

  if (!SUPABASE_READY) {
    return (
      <div className="page-container">
        <div className="card error-card">
          <p>Supabase is not configured.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="card"><p>Loading profile...</p></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="page-container">
        <h1 className="page-title">User Profile</h1>
        <div className="card error-card">
          <p>{error || 'Profile not found.'}</p>
          <p><Link to="/community">Back to Community</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">{profile.display_name}</h1>

      {/* Profile Hero */}
      <div className="card">
        <div className="profile-hero">
          <ProfilePhoto profile={profile} />
          <div className="profile-hero-info">
            <h2 className="profile-hero-name">{profile.display_name}</h2>
            <p className="profile-hero-username">@{profile.username}</p>
            <div className="profile-hero-stats">
              <div>
                <strong>{catches.length}</strong>
                <span>Catch{catches.length !== 1 ? 'es' : ''}</span>
              </div>
              <div>
                <strong>{speciesProgress.uniqueSpecies}</strong>
                <span>Species</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="page-subtitle"><Link to="/community">← Back to Community</Link></p>

      {/* Submissions Grid */}
      {catches.length === 0 ? (
        <div className="card">
          <p>No catches submitted yet.</p>
        </div>
      ) : (
        <>
          <div className="submissions-grid">
            {catches.map((catchEntry) => (
              <div
                key={catchEntry.id}
                className="submission-tile"
                onClick={() => setSelectedCatch(catchEntry)}
              >
                <CatchThumb catch={catchEntry} />
                <div className="submission-tile-info">
                  <p className="submission-tile-badge">{catchEntry.category}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Catch Modal */}
          {selectedCatch && (
            <CatchModal
              catchEntry={selectedCatch}
              onClose={() => setSelectedCatch(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function ProfilePhoto({ profile }) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    let canceled = false;

    async function loadPhoto() {
      if (!profile?.profile_photo_path || !supabase) return;
      const { data, error } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(profile.profile_photo_path, 3600);
      if (!canceled && !error && data) {
        setUrl(data.signedUrl);
      }
    }

    loadPhoto();
    return () => {
      canceled = true;
    };
  }, [profile]);

  if (!url) {
    return <div className="profile-avatar profile-avatar-fallback">👤</div>;
  }

  return <img src={url} alt={profile.display_name} className="profile-avatar" />;
}

function CatchThumb({ catch: catchEntry }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let canceled = false;

    async function loadPhoto() {
      if (!catchEntry?.photo_path || !supabase) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.storage
        .from('catch-photos')
        .createSignedUrl(catchEntry.photo_path, 3600);

      if (!canceled) {
        if (!error && data) {
          setUrl(data.signedUrl);
        }
        setLoading(false);
      }
    }

    loadPhoto();
    return () => {
      canceled = true;
    };
  }, [catchEntry]);

  if (loading) {
    return <div className="catch-thumb catch-thumb-loading">📸</div>;
  }

  if (!url) {
    return <div className="catch-thumb catch-thumb-fallback">🐟</div>;
  }

  return <img src={url} alt="Catch" className="catch-thumb" />;
}

function CatchModal({ catchEntry, onClose }) {
  const [photoUrl, setPhotoUrl] = useState('');

  useEffect(() => {
    if (!catchEntry?.photo_path || !supabase) return;

    async function loadPhoto() {
      const { data, error } = await supabase.storage
        .from('catch-photos')
        .createSignedUrl(catchEntry.photo_path, 3600);
      if (!error && data) {
        setPhotoUrl(data.signedUrl);
      }
    }

    loadPhoto();
  }, [catchEntry]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <>
      <div className="profile-catch-modal-overlay" onClick={onClose} />
      <div className="profile-catch-modal" role="dialog" aria-modal="true">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          ✕
        </button>
        <div className="modal-content">
          {photoUrl && (
            <img src={photoUrl} alt="Catch detail" className="modal-photo" />
          )}
          <div className="modal-info">
            <h3>{catchEntry.species}</h3>
            <p><strong>Category:</strong> {catchEntry.category}</p>
            <p><strong>Length:</strong> {catchEntry.length_inches} inches</p>
            <p><strong>Description:</strong> {catchEntry.description}</p>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserProfile;
